import { Link } from 'react-router-dom'
import {
  Image,
  Megaphone,
  PenLine,
  Download,
  Share2,
  Zap,
  Package,
  TrendingUp,
} from 'lucide-react'
import { useMarketingDashboard } from '../../hooks/useMarketing'
import { Card, CardContent } from '../../components/ui/Card'
import { useBrands, useProducts } from '../../hooks/useApi'

function StatCard({
  title,
  value,
  icon,
  color,
}: {
  title: string
  value: number | string
  icon: React.ReactNode
  color: string
}) {
  return (
    <div className={`${color} rounded-xl p-5 text-white shadow-md`}>
      <div className="flex justify-between items-start">
        <div>
          <p className="text-white/80 text-sm">{title}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
        </div>
        <div className="bg-white/20 p-2 rounded-lg">{icon}</div>
      </div>
    </div>
  )
}

export default function MarketingDashboard() {
  const { data, isLoading } = useMarketingDashboard()
  const { data: brands } = useBrands()
  const { data: products } = useProducts()

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Memuat dashboard...</div>
  }

  const productMap = new Map(products?.map((p: { id: number; name: string }) => [p.id, p.name]) ?? [])
  const aiBoost = data?.aiBoost
  const storage = data?.storage

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white">
        <h1 className="text-2xl font-bold">Dashboard Marketing</h1>
        <p className="text-indigo-100 mt-1">Kelola seluruh materi promosi UMKM Anda dalam satu tempat</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard title="Banner" value={data?.bannerCount ?? 0} icon={<Image className="w-5 h-5" />} color="bg-blue-600" />
        <StatCard title="Campaign" value={data?.campaignCount ?? 0} icon={<Megaphone className="w-5 h-5" />} color="bg-purple-600" />
        <StatCard title="Caption" value={data?.captionCount ?? 0} icon={<PenLine className="w-5 h-5" />} color="bg-pink-600" />
        <StatCard title="Download" value={data?.downloadCount ?? 0} icon={<Download className="w-5 h-5" />} color="bg-emerald-600" />
        <StatCard title="Share" value={data?.shareCount ?? 0} icon={<Share2 className="w-5 h-5" />} color="bg-orange-600" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-amber-500" />
              <h3 className="font-semibold">Progress AI Boost</h3>
            </div>
            {aiBoost && (
              <>
                <div className="h-3 bg-amber-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-500 rounded-full"
                    style={{
                      width: `${aiBoost.yearlyQuota ? Math.min(100, ((aiBoost.yearlyQuota - aiBoost.yearlyRemaining) / aiBoost.yearlyQuota) * 100) : 0}%`,
                    }}
                  />
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  {aiBoost.balance} kredit tersisa · {aiBoost.yearlyUsed}/{aiBoost.yearlyQuota} kuota tahunan terpakai
                </p>
                <Link to="/marketing/ai-boost" className="text-sm text-indigo-600 hover:underline mt-2 inline-block">
                  Beli AI Boost tambahan →
                </Link>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-5 h-5 text-indigo-500" />
              <h3 className="font-semibold">Progress Penyimpanan</h3>
            </div>
            {storage && (
              <>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-indigo-500 rounded-full"
                    style={{
                      width: storage.limit ? `${Math.min(100, (storage.used / storage.limit) * 100)}%` : '10%',
                    }}
                  />
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  {storage.used} / {storage.limit ?? '∞'} materi promosi
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-green-500" />
              <h3 className="font-semibold">Produk Paling Sering Dipromosikan</h3>
            </div>
            {data?.topProducts?.length ? (
              <ul className="space-y-2">
                {data.topProducts.slice(0, 5).map((p: { productId: number; count: number }) => (
                  <li key={p.productId} className="flex justify-between text-sm">
                    <span>{productMap.get(p.productId) || `Produk #${p.productId}`}</span>
                    <span className="text-gray-500">{p.count}x</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">Belum ada data promosi produk</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <h3 className="font-semibold mb-4">Produk Belum Pernah Dipromosikan</h3>
            {data?.neverPromoted?.length ? (
              <ul className="space-y-2">
                {data.neverPromoted.slice(0, 5).map((p: { id: number; name: string }) => (
                  <li key={p.id} className="text-sm text-gray-700">{p.name}</li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">Semua produk sudah pernah dipromosikan</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="pt-6">
            <h3 className="font-semibold mb-4">Banner Terbaru</h3>
            {data?.recentBanners?.length ? (
              <ul className="space-y-2">
                {data.recentBanners.map((b: { id: number; title: string; createdAt: string }) => (
                  <li key={b.id} className="flex justify-between text-sm">
                    <span className="truncate">{b.title}</span>
                    <span className="text-gray-400 shrink-0 ml-2">
                      {new Date(b.createdAt).toLocaleDateString('id-ID')}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">Belum ada banner</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <h3 className="font-semibold mb-4">Campaign Terbaru</h3>
            {data?.recentCampaigns?.length ? (
              <ul className="space-y-2">
                {data.recentCampaigns.map((c: { id: number; title: string; createdAt: string }) => (
                  <li key={c.id} className="flex justify-between text-sm">
                    <span className="truncate">{c.title}</span>
                    <span className="text-gray-400 shrink-0 ml-2">
                      {new Date(c.createdAt).toLocaleDateString('id-ID')}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">Belum ada campaign</p>
            )}
          </CardContent>
        </Card>
      </div>

      {brands?.length === 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6 text-center">
            <p className="text-amber-800">Buat brand dan produk terlebih dahulu untuk mulai mempromosikan.</p>
            <Link to="/brand" className="text-indigo-600 font-medium hover:underline mt-2 inline-block">
              Kelola Brand →
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
